import React from 'react';
import styled, { keyframes } from 'styled-components';

const glow = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
`;

const float = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(2px, -5px) rotate(1deg); }
  66% { transform: translate(-2px, -3px) rotate(-1deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;

const Section = styled.section`
  padding: 5rem 5%;
  background: #0A3B2D;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 30%, rgba(255, 214, 0, 0.06) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(76, 175, 80, 0.04) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.02) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(255, 214, 0, 0.02) 90deg, transparent 180deg, rgba(76, 175, 80, 0.02) 270deg, transparent 360deg);
    animation: ${glow} 12s ease-in-out infinite;
    pointer-events: none;
    z-index: 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  font-size: 2.75rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: white;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: -0.02em;
  position: relative;
  background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 50%, #ffffff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 4px 20px rgba(255, 255, 255, 0.1);

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #FFD600 0%, #4CAF50 100%);
    border-radius: 2px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.85);
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.7;
  font-weight: 400;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const MissionStatement = styled.div`
  text-align: center;
  margin-bottom: 5rem;
  position: relative;
  padding: 3rem 2rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  backdrop-filter: blur(20px);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, transparent 100%);
    border-radius: 24px;
    pointer-events: none;
  }
`;

const MissionTitle = styled.h3`
  font-size: 2.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #fff;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: -0.01em;
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const MissionText = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.8;
  font-weight: 400;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 3rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const TeamMember = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 24px;
  padding: 3rem 2.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(20px);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  animation: ${float} 8s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 24px;
  }

  &:hover {
    transform: translateY(-12px) scale(1.02);
    border-color: rgba(255, 214, 0, 0.3);
    box-shadow:
      0 20px 40px rgba(0, 0, 0, 0.2),
      0 8px 16px rgba(255, 214, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);

    &::before {
      opacity: 1;
    }
  }

  & > * {
    position: relative;
    z-index: 2;
  }
`;

const MemberImage = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255, 214, 0, 0.2) 0%, rgba(76, 175, 80, 0.2) 100%);
  margin: 0 auto 2rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3.5rem;
  color: white;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  ${TeamMember}:hover & {
    transform: scale(1.1) rotate(5deg);
    background: linear-gradient(135deg, rgba(255, 214, 0, 0.3) 0%, rgba(76, 175, 80, 0.3) 100%);
    box-shadow:
      0 12px 32px rgba(0, 0, 0, 0.2),
      0 4px 12px rgba(255, 214, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);

    &::before {
      opacity: 1;
    }
  }
`;

const MemberName = styled.h3`
  font-size: 1.6rem;
  margin-bottom: 0.5rem;
  color: white;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  letter-spacing: -0.01em;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  ${TeamMember}:hover & {
    transform: translateY(-2px);
  }
`;

const MemberTitle = styled.p`
  color: rgba(255, 214, 0, 0.9);
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: all 0.3s ease;

  ${TeamMember}:hover & {
    color: rgba(255, 214, 0, 1);
    transform: translateY(-1px);
  }
`;

const MemberBio = styled.p`
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.8;
  font-size: 1.05rem;
  font-weight: 400;
  transition: all 0.3s ease;

  ${TeamMember}:hover & {
    color: rgba(255, 255, 255, 0.95);
  }
`;

const TeamSection = () => {
  return (
    <Section id="team">
      <Container>
        <SectionHeader>
          <SectionTitle>Our Team</SectionTitle>
          <SectionSubtitle>
            Exceptional expertise and unwavering professionalism: Our team delivers tailored solutions
            that set new standards in employee benefits and risk management.
          </SectionSubtitle>
        </SectionHeader>

        <MissionStatement>
          <MissionTitle>We accelerate the goals of employee benefits in the corporate landscape</MissionTitle>
          <MissionText>
            Our mission is to revolutionize how businesses approach employee benefits, creating
            sustainable value for both employers and employees through innovation and expertise.
          </MissionText>
        </MissionStatement>

        <TeamGrid>
          <TeamMember delay="0s">
            <MemberImage>NM</MemberImage>
            <MemberName>Nathaniel Moth</MemberName>
            <MemberTitle>Founder & CEO</MemberTitle>
            <MemberBio>
              With a passion for transforming employee benefits, Nathaniel leads our team with
              a vision to create meaningful impact in the corporate benefits landscape.
            </MemberBio>
          </TeamMember>

          {/* Add more team members here as needed */}

        </TeamGrid>
      </Container>
    </Section>
  );
};

export default TeamSection;
